import {
  createContext,
  FC,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useCurrentModel } from "../CurrentModelContext";
import { useAnimations } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { DEV_MODE } from "../../DevConstants";
import { applyContextPresets, getUserDataProperties } from "../FeatureUtils";
import { AnimationContextPresets } from "./ContextPresets";
import {
  CommandConfig,
  CommandContext,
  executeCommands,
} from "../../Commands/CommandUtils";
import { useLevelController } from "../../Commands/LevelController";

export const FPS = 24;
export const QUANTIZED_FRAME_RANGE = 5;
export const TIME_PER_FRAME_MS = 1000 / FPS;
export const TIME_RANGE_MS = TIME_PER_FRAME_MS * QUANTIZED_FRAME_RANGE;

export const quantizeFrameToTimeRange = (frameNumber: number): number =>
  parseFloat(
    (
      (frameNumber - (frameNumber % QUANTIZED_FRAME_RANGE)) *
      TIME_PER_FRAME_MS
    ).toFixed(5)
  );

export const quantizeTimeToTimeRange = (time: number): number => {
  const timeInMS = time * 1000;

  return parseFloat((timeInMS - (timeInMS % TIME_RANGE_MS)).toFixed(5));
};

export type FrameCommandConfig = CommandConfig & {
  FrameNumber: string | number;
};

export type AnimationsControllerContextType = {
  allAnimations: string[];
  animationsMapRef: MutableRefObject<Record<string, string[]>>;
  addAnimationsToContext: (contextName: string, animations: string[]) => void;
  removeAnimationsFromContext: (
    contextName: string,
    animations: string[]
  ) => void;
  playAnimations: (animations: string[]) => void;
  stopAnimations: (animations: string[]) => void;
};

const AnimationsControllerContext =
  createContext<AnimationsControllerContextType>({
    allAnimations: [],
    animationsMapRef: { current: {} } as any,
    addAnimationsToContext: () => {},
    removeAnimationsFromContext: () => {},
    playAnimations: () => {},
    stopAnimations: () => {},
  });

const { Provider } = AnimationsControllerContext;

export const BASE_ANIMATION_CONTEXTS = {
  Playing: "Playing",
  Stopped: "Stopped",
};

export const useAnimationsController = () =>
  useContext(AnimationsControllerContext);

export const AnimationsController: FC<PropsWithChildren> = ({ children }) => {
  const {
    animations,
    scene,
    parser: { json: { animations: unparsedAnims = [] } = {} } = {},
  } = useCurrentModel();
  const animExtrasMap = useMemo<Record<string, Record<string, any>>>(
    () =>
      unparsedAnims.reduce(
        (acc: any, { name = "", extras = {} } = {}, i: number) => ({
          ...acc,
          [name || `unknown${i}`]: extras,
        }),
        {}
      ),
    [unparsedAnims]
  );
  const animCommandMap = useMemo(() => {
    const map: Record<string, Record<string, CommandConfig[]>> = {};

    for (const aName in animExtrasMap) {
      const { FrameCommands: frameCommands = [] } = getUserDataProperties(
        animExtrasMap[aName],
        "FrameCommands"
      );
      const frameMap: Record<string, CommandConfig[]> = {};

      for (const fC of frameCommands) {
        const fCC: FrameCommandConfig = fC as any;
        const { FrameNumber: frameNumber = 0 } = fCC;
        const newFCCArr = frameMap[frameNumber] || [];

        frameMap[quantizeFrameToTimeRange(parseInt(`${frameNumber}`, 10))] = [
          ...newFCCArr,
          fCC,
        ];
      }

      map[aName] = frameMap;
    }

    return map;
  }, [animExtrasMap]);
  const { actions: unprocessedActions, names } = useAnimations(
    animations,
    scene
  );
  const actions = useMemo(() => {
    for (const n of names) {
      const a = unprocessedActions[n];
      const userData = animExtrasMap[n];

      applyContextPresets(a, userData, AnimationContextPresets, "Animation");
    }

    return unprocessedActions;
  }, [unprocessedActions, names]);
  const animationsMapRef = useRef<Record<string, string[]>>({
    [BASE_ANIMATION_CONTEXTS.Playing]: [],
    [BASE_ANIMATION_CONTEXTS.Stopped]: [],
  });
  const setAnimationsMap = useCallback(
    (newAnimationsMap: Record<string, string[]> = {}) => {
      if (DEV_MODE) {
        Object.values(newAnimationsMap)
          .reduce((acc, anims) => [...acc, ...anims], [])
          .forEach((animation) => {
            if (!names.includes(animation)) {
              console.warn(`Animation "${animation}" does not exist`, names);
            }
          });
      }

      animationsMapRef.current = newAnimationsMap;
    },
    [names]
  );
  const lastAnimMapRef = useRef<Record<string, string[]>>({});
  const addAnimationsToContext = useCallback(
    (contextName: string, animations: string[]) => {
      const prevAnimations = animationsMapRef.current[contextName] || [];
      const newAnimations = animations.reduce((acc, animation) => {
        if (!acc.includes(animation)) {
          return [...acc, animation];
        }

        return acc;
      }, prevAnimations);

      setAnimationsMap({
        ...animationsMapRef.current,
        [contextName]: newAnimations,
      });
    },
    []
  );
  const removeAnimationsFromContext = useCallback(
    (contextName: string, animations: string[]) => {
      const prevAnimations = animationsMapRef.current[contextName] || [];
      const newAnimations = prevAnimations.filter(
        (a) => !animations.includes(a)
      );

      setAnimationsMap({
        ...animationsMapRef.current,
        [contextName]: newAnimations,
      });
    },
    []
  );
  const play = useCallback(
    (animations: string[] = []) => {
      animations.forEach((animation) => {
        const action = actions[animation];

        if (action) {
          action.reset().fadeIn(0.5).play();
        }
      });
    },
    [actions]
  );
  const stop = useCallback(
    (animations: string[] = []) => {
      animations.forEach((animation) => {
        const action = actions[animation];

        if (action) {
          action.fadeOut(0.5);
        }
      });
    },
    [actions]
  );
  const playAnimations = useCallback(
    (animations: string[] = []) => {
      addAnimationsToContext(BASE_ANIMATION_CONTEXTS.Playing, animations);
      removeAnimationsFromContext(BASE_ANIMATION_CONTEXTS.Stopped, animations);
    },
    [addAnimationsToContext, removeAnimationsFromContext]
  );
  const stopAnimations = useCallback(
    (animations: string[] = []) => {
      addAnimationsToContext(BASE_ANIMATION_CONTEXTS.Stopped, animations);
      removeAnimationsFromContext(BASE_ANIMATION_CONTEXTS.Playing, animations);
    },
    [addAnimationsToContext, removeAnimationsFromContext]
  );
  const framePlayRangeTrackingMapRef = useRef<
    Record<string, Record<string, boolean>>
  >({});
  const animationContext = useMemo(
    () => ({
      allAnimations: names,
      animationsMapRef,
      addAnimationsToContext,
      removeAnimationsFromContext,
      playAnimations,
      stopAnimations,
    }),
    []
  );
  const levelContext = useLevelController();
  const commandContext = useMemo<CommandContext>(
    () => ({
      animationContext,
      levelContext,
    }),
    [animationContext, levelContext]
  );

  useFrame(() => {
    if (lastAnimMapRef.current !== animationsMapRef.current) {
      const animationsMap = animationsMapRef.current;
      const {
        [BASE_ANIMATION_CONTEXTS.Playing]: prevPlayingAnimations = [],
        [BASE_ANIMATION_CONTEXTS.Stopped]: prevStoppedAnimations = [],
      } = lastAnimMapRef.current;
      const {
        [BASE_ANIMATION_CONTEXTS.Playing]: playingAnimations = [],
        [BASE_ANIMATION_CONTEXTS.Stopped]: stoppedAnimations = [],
      } = animationsMap;
      const newPlayingAnimations = playingAnimations.filter(
        (a) => !prevPlayingAnimations.includes(a)
      );
      const newStoppedAnimations = stoppedAnimations.filter(
        (a) => !prevStoppedAnimations.includes(a)
      );

      lastAnimMapRef.current = animationsMap;

      play(newPlayingAnimations);
      stop(newStoppedAnimations);
    }

    // Frame Commands
    for (const n of names) {
      const act = actions[n];

      if (act?.isRunning()) {
        const actCmdMap = animCommandMap[n];
        const time = act.time;
        const quantizedTime = quantizeTimeToTimeRange(time);
        const { [quantizedTime]: frameCommands } = actCmdMap;
        const { [quantizedTime]: isFrameCommandExecuted = false } =
          framePlayRangeTrackingMapRef.current[n] || {};

        if (frameCommands && !isFrameCommandExecuted) {
          framePlayRangeTrackingMapRef.current[n] = {
            ...framePlayRangeTrackingMapRef.current[n],
            [quantizedTime]: true,
          };

          for (const fC of frameCommands) {
            executeCommands({} as any, fC, commandContext, true);
          }
        }
      }
    }
  });

  useEffect(() => {
    // IMPORTANT: Track Action finish to reset framePlayRangeTrackingMapRef.
    const onFinish = (e: any) => {
      framePlayRangeTrackingMapRef.current[e.action._clip.name] = {};
    };

    for (const n of names) {
      const act = actions[n];

      if (act) {
        act.getMixer().addEventListener("finished", onFinish);
      }
    }

    return () => {
      for (const n of names) {
        const act = actions[n];

        if (act) {
          act.getMixer().removeEventListener("finished", onFinish);
        }
      }
    };
  }, [names, actions]);

  return <Provider value={animationContext}>{children}</Provider>;
};
